import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import PageHeader from '../../components/PageHeader';
import ReactMultiSelect from '../../components/react-select-alloption';

const InstituteStudentAttendanceReport = () => {
  const navigate = useNavigate();
  
  // API Configuration
  const API_BASE_URL = process.env.REACT_APP_API_LMS;
  const API_TOKEN = process.env.REACT_APP_API_TOKEN;
  const INSTITUTE_ID = sessionStorage.getItem("instituteId");

  // State Management
  const [programs, setPrograms] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter States
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Fetch Programs on component mount
  useEffect(() => {
    fetchPrograms();
    fetchStudents(); // Fetch all students on mount
  }, []);

  // Fetch Students when programs are selected
  useEffect(() => {
    fetchStudents();
    setSelectedStudent(null); // Reset student selection when program changes
  }, [selectedPrograms]);

  // Fetch Attendance when filters change
  useEffect(() => {
    fetchAttendance();
  }, [selectedPrograms, selectedStudent, startDate, endDate, currentPage, perPage]);

  // API Functions
  const fetchPrograms = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/institutes/${INSTITUTE_ID}/programs`, {
        method: 'GET',
        headers: {
          'Authorization': API_TOKEN,
          'Accept': 'application/json'
        }
      });
      const result = await response.json();
      if (result.status) {
        setPrograms(result.data);
      }
    } catch (err) {
      setError('Failed to fetch programs');
      console.error(err);
    }
  };

  const fetchStudents = async () => {
    try {
      let programIds;
      
      // Check if programs are selected
      if (selectedPrograms && selectedPrograms.length > 0) {
        // Check if "All Programs" is selected
        const hasAllPrograms = selectedPrograms.some(prog => prog.value === 'all');
        
        if (hasAllPrograms) {
          programIds = 'all';
        } else {
          programIds = selectedPrograms.map(prog => prog.value);
        }
      } else {
        // No programs selected - fetch all students for the institution
        programIds = null;
      }

      console.log('Fetching students for program IDs:', programIds); // Debug log

      const requestBody = {
        institution_id: INSTITUTE_ID
      };

      // Only add program_ids if programs are selected
      if (programIds !== null) {
        requestBody.program_ids = programIds;
      }

      const response = await fetch(`${API_BASE_URL}/api/students/by-program`, {
        method: 'POST',
        headers: {
          'Authorization': API_TOKEN,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      const result = await response.json();
      console.log('Students API response:', result); // Debug log
      
      if (result.status) {
        setStudents(result.data);
      }
    } catch (err) {
      setError('Failed to fetch students');
      console.error('Student fetch error:', err);
    }
  };

  const fetchAttendance = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let programIds = [];
      
      // Check if "All Programs" is selected or no programs selected
      const hasAllPrograms = selectedPrograms.some(prog => prog.value === 'all');
      
      if (!hasAllPrograms && selectedPrograms.length > 0) {
        programIds = selectedPrograms.map(prog => prog.value);
      }

      const requestBody = {
        institute_id: INSTITUTE_ID,
        per_page: perPage,
        page: currentPage
      };

      if (programIds.length > 0) {
        requestBody.program_ids = programIds;
      }

      if (selectedStudent) {
        requestBody.student_id = selectedStudent.value.toString();
      }

      if (startDate) {
        requestBody.start_date = startDate;
      }

      if (endDate) {
        requestBody.end_date = endDate;
      }

      console.log('Fetching attendance with body:', requestBody); // Debug log

      const response = await fetch(`${API_BASE_URL}/api/attendance`, {
        method: 'POST',
        headers: {
          'Authorization': API_TOKEN,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      
      if (result.success) {
        setAttendanceData(result.data.data);
        setTotalPages(result.data.last_page);
        setTotalRecords(result.data.total);
      } else {
        setError('Failed to fetch attendance data');
      }
    } catch (err) {
      setError('Failed to fetch attendance data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Format time from seconds to minutes and seconds
  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Format date from API response
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Handle View Progress
  const handleViewProgress = (studentId) => {
    navigate(`/student-enrollment/${studentId}`);
  };

  // Reset all filters
  const handleClearFilters = () => {
    setSelectedPrograms([]);
    setSelectedStudent(null);
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Prepare options for react-select
  const programOptions = useMemo(() => {
    const options = programs.map(prog => ({
      value: prog.id,
      label: prog.program_title
    }));
    return [...options];
  }, [programs]);

  const studentOptions = useMemo(() => {
    return students.map(student => ({
      value: student.id,
      label: `${student.first_name} ${student.last_name}`
    }));
  }, [students]);

  // React-Select custom styles
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
      borderColor: '#dee2e6'
    }),
    option: (base, state) => ({
      ...base,
      display: 'flex',
      alignItems: 'center',
      padding: '8px 12px'
    })
  };

  // Custom option component with checkbox
  const CheckboxOption = (props) => {
    return (
      <div
        {...props.innerProps}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          cursor: 'pointer',
          backgroundColor: props.isFocused ? '#f8f9fa' : 'white'
        }}
      >
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => {}}
          style={{ marginRight: '8px' }}
        />
        <label style={{ margin: 0, cursor: 'pointer' }}>
          {props.label}
        </label>
      </div>
    );
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <>
      {/* Header */}
       <div className="row">
          <div className="col-12">
            <PageHeader title="Student Attendance Report" 
              breadcrumbs={[
                { label: "Dashboard", path: "/institute-dashboard" },
                { label: "Student Attendance Report", active: true }
              ]}
            />
          </div>
        </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Filters Section */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            {/* Program Title */}
            <div className="col-md-3">
              <label className="form-label fw-semibold">Program</label>
              <ReactMultiSelect
                options={programOptions}
                value={selectedPrograms}
                onChange={setSelectedPrograms}
                placeholder="Select programs..."
                isMulti
                components={{ Option: CheckboxOption }}
                styles={selectStyles}
                selectMessage="program selected"
                closeMenuOnSelect={false}
                blurInputOnSelect={false}
                menuShouldBlockScroll={false}
                onBlur={() => {}}
              />
            </div>

            {/* Student Name */}
            <div className="col-md-3">
              <label className="form-label fw-semibold">Student</label>
              <Select
                options={studentOptions}
                value={selectedStudent}
                onChange={setSelectedStudent}
                placeholder="Select student..."
                isClearable
                styles={selectStyles}
              />
            </div>

            {/* Start Date */}
            <div className="col-md-2">
              <label className="form-label fw-semibold">Start Date</label>
              <input 
                type="date" 
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            
            {/* End Date */}
            <div className="col-md-2">
              <label className="form-label fw-semibold">End Date</label>
              <input 
                type="date" 
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            {/* Clear Filters Button */}
            <div className="col-md-2 d-flex align-items-end justify-content-end">
              <button 
                className="btn btn-primary"
                onClick={handleClearFilters}
              >
                <i className="fas fa-redo me-2"></i>Reset Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="card shadow-sm">
        <div className="card-header bg-white">
          <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center">
            <h5 className="mb-0 mb-md-0">
              <i className="fas fa-table me-2"></i>
              Attendance Records ({totalRecords})
            </h5>
            <div className="d-flex align-items-center justify-content-end mt-2 mt-md-0">
              <label className="me-2 mb-0 d-sm-inline">Show per page:</label>
              <select 
                className="form-select form-select-sm" 
                style={{ width: 'auto' }}
                value={perPage}
                onChange={handlePerPageChange}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover table-striped mb-0">
              <thead className="table-light">
                <tr>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Program Title</th>
                  <th>Attendance Date</th>
                  <th>Time Spent</th>
                  <th>Status</th>
                  <th>Remarks</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 mb-0">Loading attendance data...</p>
                    </td>
                  </tr>
                ) : attendanceData.length > 0 ? (
                  attendanceData.map((record, index) => (
                    <tr key={`${record.id}-${index}`}>
                      <td>{record.first_name}</td>
                      <td>{record.last_name}</td>
                      <td className="text-wrap" style={{ maxWidth: '250px' }}>
                        {record.program_title}
                      </td>
                      <td>{formatDate(record.attendance_date)}</td>
                      <td>{formatTime(Number(record.time_spend))}</td>
                      <td>
                        <span className={`badge ${
                          record.status === 'present' 
                            ? 'bg-success' 
                            : 'bg-danger'
                        }`}>
                          {record.status}
                        </span>
                      </td>
                      <td>{record.remarks}</td>
                      <td>
                        <button
                          className="btn btn-sm text-white"
                          style={{ backgroundColor: '#8b5cf6', borderColor: '#8b5cf6' }}
                          onClick={() => handleViewProgress(record.student_id)}
                        >
                          View Progress
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-5 text-muted">
                      <i className="fas fa-search me-2"></i>
                      No records found matching your filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {!loading && attendanceData.length > 0 && (
          <div className="card-footer bg-white">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2">
              <div className="text-muted text-center text-sm-start">
                Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalRecords)} of {totalRecords} entries
              </div>
              
              <nav className="mt-2 mt-sm-0">
                <ul className="pagination mb-0 flex-wrap justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      aria-label="Previous"
                    >
                      <span className="d-none d-md-inline">Previous</span>
                      <span className="d-md-none">&laquo;</span>
                    </button>
                  </li>
                  
                  {getPageNumbers().map((page, index) => (
                    <li 
                      key={index} 
                      className={`page-item ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}
                    >
                      {page === '...' ? (
                        <span className="page-link">...</span>
                      ) : (
                        <button 
                          className="page-link"
                          onClick={() => handlePageChange(page)}
                          aria-label={`Page ${page}`}
                        >
                          {page}
                        </button>
                      )}
                    </li>
                  ))}
                  
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button 
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      aria-label="Next"
                    >
                      <span className="d-none d-md-inline">Next</span>
                      <span className="d-md-none">&raquo;</span>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default InstituteStudentAttendanceReport;